import { useCallback, useEffect, useRef, useState } from "react";
import lottoApi from "./api";
import { useAuth } from "../contexts/JWTContext";

/*
 * รีวิวลูกค้า — ตรรกะฝั่งลูกค้า (ใช้ร่วมกันทั้ง landing และ landing-ver2; ไฟล์นี้เหมือนกันทั้งสองแอป)
 * หน้าตาแยกกันที่ pages/Reviews.js (landing = กำแพงคำชมฟองคำพูด · landing-ver2 = แดชบอร์ดคะแนนนีออน)
 *   • ดูรีวิวได้ทุกคน (รวมผู้เยี่ยมชม) · เขียนรีวิวต้องล็อกอิน · ทุกรีวิวรอแอดมินอนุมัติก่อนแสดง
 *   • เซิร์ฟเวอร์ตัดสินทุกอย่าง (ส่งได้ไหม พักกี่ชั่วโมง เปิด-ปิดระบบ ปิดบังชื่อ) — ฝั่งนี้แค่แสดงผลและส่งรีวิว
 */

export const MAX_MESSAGE = 1000;

/** หน้าตา/คำอธิบายตามจำนวนดาว */
export const FACES = {
  5: { icon: "fluent-emoji-flat:star-struck", label: "ยอดเยี่ยม", color: "#f59e0b" },
  4: { icon: "fluent-emoji-flat:grinning-face-with-smiling-eyes", label: "ดี", color: "#10b981" },
  3: { icon: "fluent-emoji-flat:neutral-face", label: "พอใช้", color: "#0ea5e9" },
  2: { icon: "fluent-emoji-flat:slightly-frowning-face", label: "ไม่ค่อยดี", color: "#f97316" },
  1: { icon: "fluent-emoji-flat:loudly-crying-face", label: "แย่", color: "#f43f5e" },
};
export const faceOf = (n) => FACES[Math.max(1, Math.min(5, Number(n) || 5))];

/** วลีสำเร็จรูป แตะแล้วเติมลงช่องข้อความ */
export const QUICK_PHRASES = ["ถอนเงินไว", "จ่ายจริง", "ฝากง่าย", "บริการดี", "เว็บมั่นคง", "แอดมินตอบไว"];

export const STATUS_LABEL = {
  PENDING: { label: "รอตรวจสอบ", tone: "wait" },
  APPROVED: { label: "แสดงอยู่", tone: "ok" },
  HIDDEN: { label: "ไม่แสดง", tone: "off" },
};

/** "2026-10-30T09:00:00Z" → "30 ต.ค. 69" */
export function dateText(v) {
  if (!v) return "";
  const d = new Date(typeof v === "string" ? v.replace(" ", "T") : v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("th-TH", { day: "numeric", month: "short", year: "2-digit" });
}

/** ตัวอักษรแรกของชื่อที่ถูกปิดบัง (ใช้ทำอวาตาร์) */
export const initialOf = (name) => (String(name || "").replace(/\*/g, "").trim()[0] || "?").toUpperCase();

/** สีอวาตาร์คงที่ต่อชื่อ (hue 0–359) */
export function hueOf(name) {
  let h = 0;
  String(name || "").split("").forEach((c) => {
    h = (h * 31 + c.charCodeAt(0)) % 360;
  });
  return h;
}

/** เวลารอส่งใหม่ (วินาที) → "ส่งใหม่ได้อีก 5 ชม." */
export function waitText(sec) {
  const s = Number(sec) || 0;
  if (s <= 0) return "";
  if (s >= 3600) return `ส่งใหม่ได้อีก ${Math.ceil(s / 3600)} ชม.`;
  return `ส่งใหม่ได้อีก ${Math.max(1, Math.ceil(s / 60))} นาที`;
}

const EMPTY_SUMMARY = { total: 0, avg: 0, distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }, percent: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } };
const PAGE = 8;

export function useReviews() {
  const { user, isAuthenticated } = useAuth();
  const uid = isAuthenticated ? user?.id : null;
  const [pub, setPub] = useState(null); // null = ยังโหลดไม่เสร็จ { enabled, summary, items[], page, totalPages }
  const [mine, setMine] = useState(null); // { enabled, can_submit, items[] }
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { ok:true } | { ok:false, code, message, wait_seconds }
  const alive = useRef(true);
  const inFlight = useRef(false);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const loadPublic = useCallback(() => {
    return Promise.resolve()
      .then(() => lottoApi.publicReviews(1, PAGE))
      .then((r) => alive.current && setPub({ enabled: r.enabled !== false, summary: r.summary || EMPTY_SUMMARY, items: r.data || [], page: 1, totalPages: r.pagination?.totalPages || 0 }))
      .catch(() => alive.current && setPub((prev) => prev || { enabled: true, summary: EMPTY_SUMMARY, items: [], page: 1, totalPages: 0, failed: true }));
  }, []);

  const loadMine = useCallback(() => {
    if (!uid) return Promise.resolve();
    return Promise.resolve()
      .then(() => lottoApi.myReviews())
      .then((v) => alive.current && setMine(v || null))
      .catch(() => alive.current && setMine((prev) => prev || null));
  }, [uid]);

  useEffect(() => {
    loadPublic();
  }, [loadPublic]);

  useEffect(() => {
    setMine(null);
    setResult(null);
    loadMine();
  }, [loadMine]);

  const loadMore = useCallback(async () => {
    if (!pub || pub.page >= pub.totalPages || more) return;
    setMore(true);
    try {
      const next = pub.page + 1;
      const r = await lottoApi.publicReviews(next, PAGE);
      if (alive.current) setPub((p) => ({ ...p, items: [...p.items, ...(r.data || []).filter((x) => !p.items.some((y) => y.id === x.id))], page: next, totalPages: r.pagination?.totalPages || p.totalPages }));
    } catch (_) {
      /* ลองใหม่ได้ด้วยการกดอีกครั้ง */
    } finally {
      if (alive.current) setMore(false);
    }
  }, [pub, more]);

  /** ส่งรีวิว → true เมื่อสำเร็จ */
  const submit = useCallback(
    async (rating, message) => {
      if (inFlight.current) return false;
      inFlight.current = true;
      setBusy(true);
      setResult(null);
      let ok = false;
      try {
        await lottoApi.submitReview(rating, String(message || "").trim());
        ok = true;
        if (alive.current) setResult({ ok: true });
      } catch (err) {
        const d = err?.response?.data;
        if (alive.current) setResult({ ok: false, code: d?.code || "ERROR", message: d?.message || "ส่งรีวิวไม่สำเร็จ ลองใหม่อีกครั้ง", wait_seconds: d?.wait_seconds || 0 });
      } finally {
        inFlight.current = false;
        if (alive.current) setBusy(false);
        loadMine();
      }
      return ok;
    },
    [loadMine],
  );

  const enabled = !pub || pub.enabled !== false;
  return {
    isAuthenticated,
    loading: pub === null,
    enabled,
    summary: (pub && pub.summary) || EMPTY_SUMMARY,
    items: (pub && pub.items) || [],
    hasMore: !!pub && pub.page < pub.totalPages,
    more,
    loadMore,
    mine,
    canSubmit: !!(mine && mine.enabled !== false && mine.can_submit && mine.can_submit.ok),
    submitBlock: mine && mine.can_submit && !mine.can_submit.ok ? mine.can_submit : null,
    busy,
    submit,
    result,
    clearResult: () => setResult(null),
    reload: () => Promise.all([loadPublic(), loadMine()]),
  };
}
